import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Bird, Sparkles } from "lucide-react";
import { MascotSvg } from "@/components/FlappyMascots";
import { getOrCreateDeviceId } from "@/lib/fingerprint";
import { playerErrorMessage } from "@/lib/playerErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FlappyMascotte — skill-based "tap to flap" arcade game.
 *
 * Stages: intro (mode picker) → tutorial → countdown 3-2-1 → playing →
 *         dying (slow-mo fall) → finished.
 *
 * The actual physics + collision loop runs at requestAnimationFrame inside
 * a useRef so React doesn't re-render every frame. Score is stored in the
 * ref AND mirrored to a useState for the HUD.
 */

// ---- Tuning constants ----------------------------------------------------
const PHYSICS = {
  gravity: 1500,         // px/s² downward
  flap: -450,            // px/s upward impulse on tap
  baseSpeed: 200,        // px/s leftward scroll
  speedRamp: 3,          // px/s added per 10 points
  pipeSpacing: 300,      // px horizontally between pipe pairs
};

// Difficulty maps to gap height (in px). Bigger gap = easier.
const DIFFICULTY_GAP = { easy: 170, normal: 145, hard: 120 };

// Visual canvas size — capped on tall mobiles so the mascot stays a sane
// proportion. The actual <svg> viewBox is fixed; we scale it via CSS.
const CANVAS_W = 360;
const CANVAS_H = 540;

const MASCOT_SIZE = 48;
const MASCOT_X = 110; // x position of mascot (fixed, world scrolls left)

const PIPE_W = 60;

// Background presets. Each provides a `gradient` (CSS linear-gradient) and
// a `stars` (boolean) flag for the parallax sky.
const BACKGROUNDS = {
  noir: {
    gradient:
      "linear-gradient(180deg, #1a0b3a 0%, #0a0518 60%, #1a0b3a 100%)",
    stars: true,
    accent: "#a78bfa",
  },
  day: {
    gradient: "linear-gradient(180deg, #7dd3fc 0%, #c7d2fe 100%)",
    stars: false,
    accent: "#3b82f6",
  },
  night: {
    gradient: "linear-gradient(180deg, #0c1326 0%, #1e293b 100%)",
    stars: true,
    accent: "#60a5fa",
  },
  sunset: {
    gradient:
      "linear-gradient(180deg, #fb923c 0%, #f472b6 50%, #1e1b4b 100%)",
    stars: false,
    accent: "#fbbf24",
  },
};

export default function FlappyMascotte({
  game,
  participant,
  ctasCompleted,
  receipt,
  onAlreadyParticipated,
  onResult,
  onSubmitted,
}) {
  const { t } = useTranslation();

  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    y: CANVAS_H / 2,
    vy: 0,
    pipes: [], // [{x, gapY, scored}]
    score: 0,
    tapCount: 0,
    distance: 0, // total px scrolled
    startedAt: 0,
    running: false,
    dying: false,
    deathT: 0,
  });

  const [stage, setStage] = useState("intro"); // intro|countdown|playing|dying|finished
  const [score, setScore] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [mascotPhase, setMascotPhase] = useState(0); // wing flap 0/1
  const [yPx, setYPx] = useState(CANVAS_H / 2);
  const [pipes, setPipes] = useState([]);
  const [scorePopups, setScorePopups] = useState([]); // [{id, t}]
  const [trail, setTrail] = useState([]); // last positions for trail
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const bg = BACKGROUNDS[game.background_theme] || BACKGROUNDS.noir;
  const gapHeight = DIFFICULTY_GAP[game.flappy_difficulty] || DIFFICULTY_GAP.normal;
  const maxDuration = game.flappy_max_duration || 40;

  // ----- Game loop -----
  const tick = useCallback(
    (now) => {
      const s = stateRef.current;
      if (!s.running) return;
      const dt = Math.min(0.05, (now - s.lastFrame) / 1000);
      s.lastFrame = now;

      // Apply gravity
      s.vy += PHYSICS.gravity * dt;
      s.y += s.vy * dt;

      // Scroll world
      const speed =
        PHYSICS.baseSpeed + PHYSICS.speedRamp * Math.floor(s.score / 10);
      const scrollX = speed * dt;
      s.distance += scrollX;
      for (const p of s.pipes) {
        p.x -= scrollX;
      }

      // Spawn new pipes when needed (keep ~3 on screen)
      const lastPipe = s.pipes[s.pipes.length - 1];
      if (!lastPipe || lastPipe.x < CANVAS_W - PHYSICS.pipeSpacing) {
        const gapMargin = 80;
        const gapY =
          gapMargin +
          Math.random() * (CANVAS_H - gapMargin * 2 - gapHeight);
        s.pipes.push({
          x: CANVAS_W + 20,
          gapY,
          scored: false,
        });
      }

      // Cull off-screen pipes
      s.pipes = s.pipes.filter((p) => p.x > -PIPE_W - 10);

      // Score: pipe whose right edge just passed the mascot's left edge
      for (const p of s.pipes) {
        if (!p.scored && p.x + PIPE_W < MASCOT_X) {
          p.scored = true;
          s.score += 1;
          setScore(s.score);
          // Pop a +1
          setScorePopups((prev) => [
            ...prev.slice(-3),
            { id: Date.now() + Math.random(), t: Date.now() },
          ]);
        }
      }

      // Collisions: floor / ceiling / pipe AABB
      const top = s.y - MASCOT_SIZE / 2;
      const bot = s.y + MASCOT_SIZE / 2;
      let dead = false;
      if (bot >= CANVAS_H - 20 || top <= 0) dead = true;
      if (!dead) {
        for (const p of s.pipes) {
          if (p.x < MASCOT_X + MASCOT_SIZE / 2 && p.x + PIPE_W > MASCOT_X - MASCOT_SIZE / 2) {
            // Mascot is horizontally overlapping this pipe
            if (top < p.gapY || bot > p.gapY + gapHeight) {
              dead = true;
              break;
            }
          }
        }
      }
      // Force end at max duration as a safety net
      const elapsed = (now - s.startedAt) / 1000;
      if (elapsed >= maxDuration) dead = true;

      // Mirror to React state (throttled to once per frame)
      setYPx(s.y);
      setPipes(s.pipes.map((p) => ({ ...p })));
      setTrail((tr) => {
        const next = [...tr, { x: MASCOT_X, y: s.y, t: now }];
        return next.slice(-10);
      });

      if (dead) {
        s.running = false;
        s.dying = true;
        startDying();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [gapHeight, maxDuration],
  );

  // ----- Death sequence: slow-mo fall + screen shake -----
  const fallRafRef = useRef(null);
  function startDying() {
    const s = stateRef.current;
    const t0 = performance.now();
    const startY = s.y;
    const startVy = Math.max(s.vy, 100);
    const fall = (now) => {
      const t = (now - t0) / 1000;
      // Slow-mo gravity (×0.5)
      s.y = startY + startVy * t + 0.5 * (PHYSICS.gravity * 0.5) * t * t;
      setYPx(s.y);
      if (s.y < CANVAS_H - 20 && t < 1.6) {
        fallRafRef.current = requestAnimationFrame(fall);
      } else {
        // Submit score
        submitFinal(s.score, s.tapCount, Math.round(now - s.startedAt));
      }
    };
    // Schedule fall BEFORE setStage so the setState-induced cleanup of the
    // "playing" useEffect doesn't cancel the RAF we just queued. We use a
    // dedicated `fallRafRef` so the main-loop cleanup (`rafRef.current`)
    // can't touch it either.
    fallRafRef.current = requestAnimationFrame(fall);
    setStage("dying");
  }

  // ----- Tap handler (active only during real play) -----
  const onTap = useCallback(() => {
    if (stage === "playing") {
      const s = stateRef.current;
      s.vy = PHYSICS.flap;
      s.tapCount += 1;
      setMascotPhase((p) => (p === 0 ? 1 : 0));
    }
  }, [stage]);

  // ----- Lifecycle: countdown 3-2-1 -----
  useEffect(() => {
    if (stage !== "countdown") return;
    setCountdown(3);
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          setStage("playing");
          return 0;
        }
        return c - 1;
      });
    }, 800);
    return () => clearInterval(id);
  }, [stage]);

  // ----- Lifecycle: real game start -----
  useEffect(() => {
    if (stage !== "playing") return;
    const s = stateRef.current;
    s.y = CANVAS_H / 2;
    s.vy = 0;
    s.score = 0;
    s.tapCount = 0;
    s.pipes = [];
    s.distance = 0;
    s.startedAt = performance.now();
    s.lastFrame = performance.now();
    s.running = true;
    s.dying = false;
    setScore(0);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      // Stop the main tick loop. IMPORTANT: we do NOT touch `fallRafRef` —
      // the dying animation owns its own RAF and will submit the score.
      cancelAnimationFrame(rafRef.current);
      stateRef.current.running = false;
    };
  }, [stage, tick]);

  // ----- Submit -----
  const submitInFlightRef = useRef(false);
  async function submitFinal(score, tapCount, durationMs) {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitting(true);
    // Safety: if the network hangs, still fall back to a terminal screen
    // after 8 s so the user never stares at a falling mascot.
    const safetyId = setTimeout(() => {
      if (submitInFlightRef.current) {
        setResult({
          score,
          is_win: false,
          result_label: t("flappy.timeout", "Délai réseau dépassé"),
          error: true,
        });
        setStage("finished");
      }
    }, 8000);
    try {
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/flappy-submit`,
        {
          participant,
          ctas_completed: ctasCompleted || [],
          score,
          tap_count: tapCount,
          duration_ms: durationMs,
          fingerprint: { device_id: getOrCreateDeviceId() },
          receipt_url: receipt?.url,
          receipt_ocr: receipt?.ocr,
        },
      );
      setResult(data);
      setStage("finished");
      onSubmitted?.();
      // Do NOT call onResult yet — let the user see the ResultCard first,
      // then they tap "Voir mon cadeau" (winners) or "Fermer" (losers) to
      // open the standard prize/lose modal.
    } catch (e) {
      const detailText = e.response?.data?.detail ? playerErrorMessage(e) : "";
      if (e.response?.status === 409) {
        // Still show a final screen so the user isn't stuck on a falling
        // mascot. PublicGame will also display its standard red banner.
        setResult({
          score,
          is_win: false,
          result_label: detailText || t("errors.already_participated"),
          already_participated: true,
        });
        setStage("finished");
        onAlreadyParticipated?.(detailText || t("errors.already_participated"));
      } else {
        // Network / validation error — still show a terminal card so the
        // user knows the run ended.
        setResult({
          score,
          is_win: false,
          result_label: detailText || t("errors.score_not_counted"),
          error: true,
        });
        setStage("finished");
      }
    } finally {
      clearTimeout(safetyId);
      setSubmitting(false);
    }
  }

  // ----- Render -----
  const stars = useMemo(
    () =>
      bg.stars
        ? Array.from({ length: 30 }).map((_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            r: Math.random() * 1.5 + 0.5,
          }))
        : [],
    [bg.stars],
  );

  return (
    <div ref={wrapRef} className="relative mx-auto w-full max-w-md select-none" data-testid="flappy-stage">
      {stage === "intro" && (
        <IntroCard
          mascotPreset={game.mascot_preset || "cupcake"}
          mascotImage={game.mascot_image}
          onStart={() => setStage("countdown")}
        />
      )}

      {/* Game canvas — only while actively playing. Hidden once the run
          is over so the ResultCard takes the whole viewport and nothing
          else distracts the user. */}
      {["countdown", "playing", "dying"].includes(stage) && (
        <div
          className="relative mx-auto overflow-hidden rounded-[28px] border border-white/10 shadow-2xl"
          style={{
            width: "100%",
            maxWidth: CANVAS_W,
            aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
            background: bg.gradient,
          }}
          onPointerDown={onTap}
          data-testid="flappy-canvas"
        >
          {/* Stars layer */}
          {stars.map((s) => (
            <span
              key={s.id}
              aria-hidden
              className="absolute rounded-full bg-white/70"
              style={{
                left: `${s.x}%`,
                top: `${s.y}%`,
                width: s.r,
                height: s.r,
                boxShadow: `0 0 ${s.r * 2}px rgba(255,255,255,0.5)`,
              }}
            />
          ))}

          {/* HUD */}
          <div className="absolute left-3 right-3 top-3 z-30 flex items-center justify-between text-white">
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-black/30 px-3 py-1 text-[11px] font-bold uppercase tracking-wider backdrop-blur">
              <Bird className="h-3 w-3" />
              <span>{t("flappy.hud_score", "Score")}</span>
              <span className="ml-1 tabular-nums">{score}</span>
            </div>
          </div>

          {/* SVG world: pipes + mascot */}
          <svg
            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 h-full w-full"
            aria-hidden
          >
            {/* Trail particles */}
            {trail.map((p, i) => (
              <circle
                key={i}
                cx={p.x - (trail.length - 1 - i) * 5}
                cy={p.y}
                r={(i / trail.length) * 4}
                fill={bg.accent}
                opacity={(i / trail.length) * 0.4}
              />
            ))}

            {pipes.map((p, i) => (
              <Obstacle
                key={i}
                x={p.x}
                gapY={p.gapY}
                gapHeight={gapHeight}
                theme={game.obstacle_theme || "pipes"}
              />
            ))}

            {/* Floor strip */}
            <rect x="0" y={CANVAS_H - 18} width={CANVAS_W} height="18" fill="rgba(0,0,0,0.5)" />
            <line x1="0" y1={CANVAS_H - 18} x2={CANVAS_W} y2={CANVAS_H - 18} stroke={bg.accent} strokeWidth="1.5" opacity="0.7" />

            {/* Mascot */}
            <g
              transform={`translate(${MASCOT_X - MASCOT_SIZE / 2} ${yPx - MASCOT_SIZE / 2}) rotate(${
                Math.max(-30, Math.min(60, stateRef.current.vy / 10))
              } ${MASCOT_SIZE / 2} ${MASCOT_SIZE / 2})`}
            >
              {game.mascot_image ? (
                <image
                  href={game.mascot_image}
                  width={MASCOT_SIZE}
                  height={MASCOT_SIZE}
                  preserveAspectRatio="xMidYMid meet"
                />
              ) : (
                <foreignObject
                  width={MASCOT_SIZE}
                  height={MASCOT_SIZE}
                  style={{ overflow: "visible" }}
                >
                  <div xmlns="http://www.w3.org/1999/xhtml">
                    <MascotSvg
                      preset={game.mascot_preset || "cupcake"}
                      size={MASCOT_SIZE}
                      wingPhase={mascotPhase}
                    />
                  </div>
                </foreignObject>
              )}
            </g>

            {/* +1 score popups */}
            {scorePopups.slice(-3).map((p) => {
              const age = (Date.now() - p.t) / 600;
              if (age > 1) return null;
              return (
                <text
                  key={p.id}
                  x={MASCOT_X + 30}
                  y={CANVAS_H / 2 - 50 - age * 40}
                  fill="#fef3c7"
                  fontSize="22"
                  fontWeight="bold"
                  opacity={1 - age}
                  filter="drop-shadow(0 0 6px rgba(251,191,36,0.6))"
                >
                  +1
                </text>
              );
            })}
          </svg>

          {/* Countdown */}
          {stage === "countdown" && (
            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <motion.div
                key={countdown}
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.6, opacity: 0 }}
                className="text-7xl font-extrabold text-white drop-shadow-[0_0_24px_rgba(168,139,250,0.8)]"
                data-testid="flappy-countdown"
              >
                {countdown > 0 ? countdown : "GO !"}
              </motion.div>
            </div>
          )}

          {/* Death flash */}
          <AnimatePresence>
            {stage === "dying" && (
              <motion.div
                key="death"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.7, 0.3] }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 z-40 bg-rose-500/40 mix-blend-screen"
              />
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Final result card */}
      {stage === "finished" && (
        <ResultCard
          result={result}
          onReveal={() => onResult?.(result)}
          submitting={submitting}
        />
      )}
    </div>
  );
}

// =========================================================================
// Sub-components
// =========================================================================

function IntroCard({ mascotPreset, mascotImage, onStart }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[28px] border border-white/10 p-6 text-center backdrop-blur-2xl sm:p-8"
      style={{
        background:
          "linear-gradient(180deg, rgba(18,10,34,0.8) 0%, rgba(5,6,12,0.92) 100%)",
        boxShadow:
          "0 40px 100px -40px rgba(168,139,250,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
      data-testid="flappy-intro"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-10 mx-auto h-40 w-40 rounded-full blur-3xl"
        style={{
          background:
            "conic-gradient(from 180deg at 50% 50%, #A78BFA, #22D3EE, #F472B6, #fbbf24, #A78BFA)",
          opacity: 0.35,
        }}
      />
      <div className="relative mx-auto mb-4 flex h-24 w-24 items-center justify-center">
        <motion.div
          animate={{ y: [-6, 6, -6] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          {mascotImage ? (
            <img src={mascotImage} alt="mascot" className="h-20 w-20 rounded-xl object-cover" />
          ) : (
            <MascotSvg preset={mascotPreset} size={80} />
          )}
        </motion.div>
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.4em] text-white/50">
        {t("flappy.intro_eyebrow", "Mini-jeu")}
      </div>
      <h3 className="mt-1 text-2xl font-extrabold leading-tight text-white sm:text-[28px]">
        {t("flappy.intro_title", "Fais voler la mascotte !")}
      </h3>
      <p className="mx-auto mt-3 max-w-sm text-sm text-white/70">
        {t(
          "flappy.intro_sub",
          "Tape sur l'écran pour battre des ailes et passe entre les obstacles. Plus ton score est haut, mieux c'est !",
        )}
      </p>
      <button
        type="button"
        onClick={onStart}
        className="mt-7 w-full rounded-2xl px-5 py-3.5 text-sm font-bold text-white shadow-lg transition hover:brightness-110"
        style={{
          background:
            "linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #22d3ee 100%)",
        }}
        data-testid="flappy-intro-start"
      >
        {t("flappy.intro_btn", "Jouer maintenant")}
      </button>
    </motion.div>
  );
}

/**
 * Obstacle — renders the top + bottom obstacle pair with the configured
 * theme. The gap is between (gapY, gapY + gapHeight). Each theme uses a
 * distinct SVG shape, not just a different colour.
 */
function Obstacle({ x, gapY, gapHeight, theme }) {
  const t = theme || "pipes";
  // Top obstacle hangs from the ceiling with its bottom edge at gapY.
  // Bottom obstacle stands on the floor with its top edge at gapY+gapHeight.
  const topBottom = gapY;                     // top-obstacle bottom edge (y)
  const botTop = gapY + gapHeight;            // bottom-obstacle top edge (y)
  const floor = CANVAS_H - 18;
  // Total pixel height of each half
  const topH = Math.max(0, topBottom);
  const botH = Math.max(0, floor - botTop);

  switch (t) {
    case "forks":
      return (
        <g>
          <ForkShape x={x} y={0} h={topH} direction="down" />
          <ForkShape x={x} y={botTop} h={botH} direction="up" />
        </g>
      );
    case "cups":
      return (
        <g>
          <CupShape x={x} y={0} h={topH} direction="down" />
          <CupShape x={x} y={botTop} h={botH} direction="up" />
        </g>
      );
    case "books":
      return (
        <g>
          <BookStackShape x={x} y={0} h={topH} direction="down" />
          <BookStackShape x={x} y={botTop} h={botH} direction="up" />
        </g>
      );
    case "bottles":
      return (
        <g>
          <BottleShape x={x} y={0} h={topH} direction="down" />
          <BottleShape x={x} y={botTop} h={botH} direction="up" />
        </g>
      );
    case "buildings":
      return (
        <g>
          <BuildingShape x={x} y={0} h={topH} direction="down" />
          <BuildingShape x={x} y={botTop} h={botH} direction="up" />
        </g>
      );
    case "pipes":
    default:
      return <PipesShape x={x} gapY={gapY} gapHeight={gapHeight} />;
  }
}

/* ---- Individual obstacle renderers ------------------------------- */

// Classic green Mario-style pipes (default)
function PipesShape({ x, gapY, gapHeight }) {
  const fill = "#22c55e";
  const cap = "#15803d";
  const floor = CANVAS_H - 18;
  return (
    <g>
      <rect x={x} y={0} width={PIPE_W} height={gapY} fill={fill} stroke={cap} strokeWidth="2" />
      <rect x={x - 4} y={gapY - 18} width={PIPE_W + 8} height={18} fill={cap} rx="3" />
      <rect x={x + 4} y={2} width={6} height={Math.max(0, gapY - 22)} fill="rgba(255,255,255,0.18)" rx="2" />
      <rect
        x={x}
        y={gapY + gapHeight}
        width={PIPE_W}
        height={floor - (gapY + gapHeight)}
        fill={fill}
        stroke={cap}
        strokeWidth="2"
      />
      <rect x={x - 4} y={gapY + gapHeight} width={PIPE_W + 8} height={18} fill={cap} rx="3" />
      <rect
        x={x + 4}
        y={gapY + gapHeight + 22}
        width={6}
        height={Math.max(0, floor - (gapY + gapHeight) - 22)}
        fill="rgba(255,255,255,0.18)"
        rx="2"
      />
    </g>
  );
}

// Giant stainless-steel fork — 4 prongs at the tip, long handle
function ForkShape({ x, y, h, direction }) {
  if (h <= 0) return null;
  const down = direction === "down";
  const w = PIPE_W;
  const cx = x + w / 2;
  const handleFill = "#e2e8f0";
  const handleStroke = "#64748b";
  // Prong zone (tip of the fork) — 28 px at the tip side
  const prongH = Math.min(28, h);
  const handleH = h - prongH;
  const handleY = down ? y : y + prongH;       // rectangle Y of the handle
  const prongY = down ? y + handleH : y;       // rectangle Y of the prong base
  // Tips go AWAY from the playfield (out of the gap)
  // down: prongs point DOWN (towards gap is UP) — wait, for top obstacle the
  // tip (prongs) should face the gap. So:
  //  - top obstacle (direction=down): prongs on the bottom edge (facing the gap)
  //  - bottom obstacle (direction=up): prongs on the top edge (facing the gap)
  const prongsAtTop = !down; // true for bottom obstacle
  return (
    <g>
      {/* Handle */}
      <rect
        x={x + 10}
        y={handleY}
        width={w - 20}
        height={handleH}
        fill={handleFill}
        stroke={handleStroke}
        strokeWidth="1.5"
        rx="4"
      />
      {/* Handle grip grooves */}
      {handleH > 30 &&
        [0.25, 0.5, 0.75].map((r, i) => (
          <rect
            key={i}
            x={x + 12}
            y={handleY + handleH * r}
            width={w - 24}
            height="2"
            fill={handleStroke}
            opacity="0.5"
          />
        ))}
      {/* Prong zone base */}
      <rect
        x={x + 6}
        y={prongY}
        width={w - 12}
        height={prongH}
        fill={handleFill}
        stroke={handleStroke}
        strokeWidth="1.5"
        rx="3"
      />
      {/* 4 prongs */}
      {[0, 1, 2, 3].map((i) => {
        const prongX = x + 10 + i * ((w - 20) / 3);
        const prongW = 6;
        const prongTipY = prongsAtTop ? prongY - 8 : prongY + prongH + 8;
        const prongBaseY = prongsAtTop ? prongY + 4 : prongY + prongH - 4;
        return (
          <polygon
            key={i}
            points={`${prongX},${prongBaseY} ${prongX + prongW},${prongBaseY} ${prongX + prongW / 2},${prongTipY}`}
            fill={handleFill}
            stroke={handleStroke}
            strokeWidth="1.5"
          />
        );
      })}
      {/* Shine */}
      <rect
        x={x + 14}
        y={handleY + 4}
        width="3"
        height={Math.max(0, handleH - 8)}
        fill="rgba(255,255,255,0.7)"
        rx="1"
      />
    </g>
  );
}

// Paper coffee cup — cylinder with a lid and a brand sleeve
function CupShape({ x, y, h, direction }) {
  if (h <= 0) return null;
  const down = direction === "down";
  const w = PIPE_W;
  const body = "#f97316";
  const sleeve = "#7c2d12";
  const lid = "#1f2937";
  // Lid at the mouth of the cup (facing the gap)
  const lidH = 12;
  const lidY = down ? y + h - lidH : y;
  const bodyY = down ? y : y + lidH;
  const bodyH = h - lidH;
  return (
    <g>
      {/* Cup body - trapezoid gently narrowing away from the gap */}
      <path
        d={
          down
            ? `M ${x + 4} ${bodyY} L ${x + w - 4} ${bodyY} L ${x + w - 8} ${bodyY + bodyH} L ${x + 8} ${bodyY + bodyH} Z`
            : `M ${x + 8} ${bodyY} L ${x + w - 8} ${bodyY} L ${x + w - 4} ${bodyY + bodyH} L ${x + 4} ${bodyY + bodyH} Z`
        }
        fill={body}
        stroke="#7c2d12"
        strokeWidth="2"
      />
      {/* Sleeve band */}
      <rect
        x={x + 6}
        y={bodyY + bodyH * 0.4}
        width={w - 12}
        height={Math.min(24, bodyH * 0.3)}
        fill={sleeve}
      />
      {/* Sleeve text */}
      {bodyH > 40 && (
        <text
          x={x + w / 2}
          y={bodyY + bodyH * 0.55}
          textAnchor="middle"
          fontSize="8"
          fontWeight="bold"
          fill="#fef3c7"
          fontFamily="serif"
        >
          CAFÉ
        </text>
      )}
      {/* Lid */}
      <rect x={x + 2} y={lidY} width={w - 4} height={lidH} fill={lid} rx="2" />
      <rect x={x + 8} y={lidY + 3} width={w - 16} height="2" fill="rgba(255,255,255,0.3)" rx="1" />
      {/* Little hole in the lid */}
      <circle cx={x + w / 2} cy={lidY + lidH / 2 + 1} r="2.5" fill="#000" opacity="0.6" />
    </g>
  );
}

// Stack of books with coloured spines
function BookStackShape({ x, y, h, direction }) {
  if (h <= 0) return null;
  const w = PIPE_W;
  const colors = ["#a78bfa", "#f472b6", "#60a5fa", "#34d399", "#fbbf24", "#f97316", "#ef4444"];
  // Each book is ~28px tall. Start stacking from the gap side.
  const bookH = 28;
  const booksCount = Math.max(1, Math.floor(h / bookH));
  const down = direction === "down";
  return (
    <g>
      {Array.from({ length: booksCount }).map((_, i) => {
        // i=0 is the book closest to the gap; stack outward
        const yy = down ? y + h - (i + 1) * bookH : y + i * bookH;
        const c = colors[i % colors.length];
        // Alternate slight horizontal wobble for stack look
        const dx = i % 2 === 0 ? 0 : 3;
        return (
          <g key={i} transform={`translate(${dx} 0)`}>
            <rect
              x={x}
              y={yy + 2}
              width={w}
              height={bookH - 4}
              fill={c}
              stroke="rgba(0,0,0,0.3)"
              strokeWidth="1.5"
              rx="2"
            />
            {/* Pages */}
            <rect
              x={x + 3}
              y={yy + 4}
              width={w - 6}
              height={bookH - 8}
              fill="#fef3c7"
              opacity="0.5"
            />
            {/* Spine label */}
            <rect
              x={x + 8}
              y={yy + (bookH - 4) / 2 - 3}
              width={w - 16}
              height="6"
              fill="rgba(0,0,0,0.25)"
              rx="1"
            />
          </g>
        );
      })}
    </g>
  );
}

// Wine / beer bottle — neck, shoulder, body, label
function BottleShape({ x, y, h, direction }) {
  if (h <= 0) return null;
  const down = direction === "down";
  const w = PIPE_W;
  const cx = x + w / 2;
  const glass = "#16a34a";
  const glassDark = "#052e16";
  // Neck side faces the gap
  const neckH = Math.min(34, h * 0.35);
  const bodyH = h - neckH;
  const neckY = down ? y + h - neckH : y;
  const bodyY = down ? y : y + neckH;
  // Neck width: narrower
  const neckW = 22;
  const neckX = cx - neckW / 2;
  return (
    <g>
      {/* Body */}
      <rect x={x + 4} y={bodyY} width={w - 8} height={bodyH} fill={glass} stroke={glassDark} strokeWidth="2" rx="4" />
      {/* Shoulder curve — a polygon bridging body to neck */}
      <path
        d={
          down
            ? `M ${x + 4} ${bodyY + bodyH} Q ${cx} ${bodyY + bodyH - 14} ${neckX} ${neckY} L ${neckX + neckW} ${neckY} Q ${cx} ${bodyY + bodyH - 14} ${x + w - 4} ${bodyY + bodyH} Z`
            : `M ${x + 4} ${bodyY} Q ${cx} ${bodyY + 14} ${neckX} ${neckY + neckH} L ${neckX + neckW} ${neckY + neckH} Q ${cx} ${bodyY + 14} ${x + w - 4} ${bodyY} Z`
        }
        fill={glass}
        stroke={glassDark}
        strokeWidth="2"
      />
      {/* Neck */}
      <rect x={neckX} y={neckY} width={neckW} height={neckH} fill={glass} stroke={glassDark} strokeWidth="2" />
      {/* Cap */}
      {down ? (
        <rect x={neckX + 2} y={neckY + neckH - 8} width={neckW - 4} height="8" fill="#fbbf24" stroke={glassDark} strokeWidth="1" rx="1" />
      ) : (
        <rect x={neckX + 2} y={neckY} width={neckW - 4} height="8" fill="#fbbf24" stroke={glassDark} strokeWidth="1" rx="1" />
      )}
      {/* Label */}
      {bodyH > 50 && (
        <>
          <rect
            x={x + 10}
            y={bodyY + bodyH * 0.35}
            width={w - 20}
            height={Math.min(40, bodyH * 0.35)}
            fill="#fef3c7"
            stroke="#b45309"
            strokeWidth="1.2"
            rx="1.5"
          />
          <text
            x={cx}
            y={bodyY + bodyH * 0.55}
            textAnchor="middle"
            fontSize="8"
            fontWeight="bold"
            fill="#92400e"
            fontFamily="serif"
          >
            QRISE
          </text>
        </>
      )}
      {/* Highlight */}
      <rect x={x + 10} y={bodyY + 6} width="3" height={Math.max(0, bodyH - 12)} fill="rgba(255,255,255,0.3)" rx="1" />
    </g>
  );
}

// Skyscraper — dark tower with grid of lit windows + antenna
function BuildingShape({ x, y, h, direction }) {
  if (h <= 0) return null;
  const w = PIPE_W;
  const wall = "#1f2937";
  const wallLight = "#374151";
  const down = direction === "down";
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={wall} stroke={wallLight} strokeWidth="2" />
      {/* Vertical divider for a 2-column window layout */}
      <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} stroke={wallLight} strokeWidth="1" />
      {/* Rooftop cap (only on the "outside" edge, away from the gap) */}
      {down ? (
        <rect x={x - 4} y={y} width={w + 8} height="6" fill={wallLight} rx="1" />
      ) : (
        <rect x={x - 4} y={y + h - 6} width={w + 8} height="6" fill={wallLight} rx="1" />
      )}
      {/* Antenna */}
      {down && h > 24 && (
        <line x1={x + w / 2} y1={y - 12} x2={x + w / 2} y2={y} stroke={wallLight} strokeWidth="2" />
      )}
      {/* Window grid */}
      {Array.from({ length: Math.floor(h / 14) }).map((_, row) => {
        const rowY = y + 10 + row * 14;
        if (rowY + 8 > y + h) return null;
        return [0, 1].map((col) => {
          // Randomly (deterministically from position) light windows
          const lit = ((row * 31 + col * 17 + Math.floor(x)) % 4) !== 0;
          return (
            <rect
              key={`${row}-${col}`}
              x={x + 6 + col * (w / 2 - 2)}
              y={rowY}
              width={w / 2 - 10}
              height="8"
              fill={lit ? "#fbbf24" : "#0f172a"}
              stroke="rgba(0,0,0,0.4)"
              strokeWidth="0.8"
              rx="1"
            />
          );
        });
      })}
    </g>
  );
}

function ResultCard({ result, onReveal, submitting }) {
  const { t } = useTranslation();
  if (submitting || !result) {
    return (
      <div className="mx-auto max-w-md rounded-[28px] border border-white/10 bg-black/60 p-8 text-center backdrop-blur" data-testid="flappy-submitting">
        <div className="text-sm text-white/70">
          {t("flappy.submitting", "Calcul du score…")}
        </div>
      </div>
    );
  }
  const isTournament = result.tournament?.mode === "tournament";
  const isWin = !!result.is_win;

  // ---- Tournament result: show rank instead of instant prize ----
  if (isTournament) {
    const { rank, total, winners_count, end_date } = result.tournament;
    const inTop = rank <= winners_count;
    const endLabel = end_date
      ? new Date(end_date).toLocaleDateString(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : null;
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-auto max-w-md rounded-[28px] border border-white/10 p-6 text-center backdrop-blur-2xl"
        style={{
          background:
            "linear-gradient(180deg, rgba(18,10,34,0.85) 0%, rgba(5,6,12,0.95) 100%)",
          boxShadow: "0 40px 100px -40px rgba(168,139,250,0.55)",
        }}
        data-testid="flappy-result-tournament"
      >
        <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/50">
          {t("flappy.result_eyebrow", "Score final")}
        </div>
        <div className="mt-2 text-6xl font-extrabold tabular-nums text-white drop-shadow-[0_0_24px_rgba(168,139,250,0.7)]">
          {result.score ?? 0}
        </div>
        <div
          className={`mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
            inTop
              ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/40"
              : "bg-white/5 text-white/80 border border-white/10"
          }`}
          data-testid="flappy-rank-badge"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t("flappy.rank", "Tu es {{rank}}e sur {{total}}", {
            rank,
            total,
          })}
        </div>
        <div className="mt-4 text-[12.5px] text-white/65 leading-relaxed px-4">
          {inTop
            ? t(
                "flappy.rank_in_top",
                "Tu es actuellement dans le top {{n}} ! On t'envoie un email si tu es toujours gagnant à la fin du concours.",
                { n: winners_count },
              )
            : t(
                "flappy.rank_out_top",
                "Les {{n}} meilleurs scores gagnent. Continue à jouer… ah non, une seule participation par personne 😄 Croisons les doigts !",
                { n: winners_count },
              )}
        </div>
        {endLabel && (
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-white/50">
            {t("flappy.tournament_ends", "Fin du concours : {{date}}", {
              date: endLabel,
            })}
          </div>
        )}
      </motion.div>
    );
  }

  // ---- Tier mode: unchanged ----
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mx-auto max-w-md rounded-[28px] border border-white/10 p-6 text-center backdrop-blur-2xl"
      style={{
        background:
          "linear-gradient(180deg, rgba(18,10,34,0.85) 0%, rgba(5,6,12,0.95) 100%)",
        boxShadow: "0 40px 100px -40px rgba(168,139,250,0.55)",
      }}
      data-testid="flappy-result"
    >
      <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/50">
        {t("flappy.result_eyebrow", "Score final")}
      </div>
      <div className="mt-2 text-6xl font-extrabold tabular-nums text-white drop-shadow-[0_0_24px_rgba(168,139,250,0.7)]">
        {result.score ?? 0}
      </div>
      {isWin ? (
        <>
          <div className="mt-4 text-sm font-bold text-emerald-300 uppercase tracking-wider">
            {t("flappy.result_win", "Tu as gagné !")}
          </div>
          <div className="mt-1 text-lg font-semibold text-white">{result.result_label}</div>
          <button
            type="button"
            onClick={onReveal}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-500 via-pink-500 to-cyan-400 px-6 py-3 text-sm font-bold text-white shadow-xl hover:brightness-110"
            data-testid="flappy-result-reveal"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t("flappy.result_reveal", "Voir mon cadeau")}
          </button>
        </>
      ) : (
        <>
          <div className="mt-4 text-sm font-bold text-white/70 uppercase tracking-wider">
            {t("flappy.result_lose", "Pas de lot cette fois")}
          </div>
          <div className="mt-1 text-[13px] text-white/60">
            {t(
              "flappy.result_lose_sub",
              "Il fallait atteindre le palier minimum. Reviens une prochaine fois !",
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}
